import React, { useState, useEffect } from 'react';
import { firebaseAppointmentService, Appointment, ProviderAvailability } from '../../../services/firebase/FirebaseAppointmentService';
import TimeBlockView from '../../TimeBlockView';
import MultiMonthCalendar from '../../MultiMonthCalendar';
import FormAssignmentService from '../../../services/FormAssignmentService';
import CompanyFormSettingsService from '../../../services/CompanyFormSettingsService';
import VoiceInputButton from '../../VoiceInputButton';
import { useToast } from '../../Toast';
import FirebaseActivityService from '../../../services/firebase/FirebaseActivityService';
import EmailNotificationService from '../../../services/EmailNotificationService';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { FirebaseCoupleService, CoupleRecord } from '../../../services/firebase/FirebaseCoupleService';

interface BookAppointmentTabProps {
  patientId: string;
  patientName: string;
  providerId?: string;
  rescheduleAppointmentId?: string | null;
}

// Helper function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time24: string): string => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const BookAppointmentTab: React.FC<BookAppointmentTabProps> = ({ patientId, patientName, providerId: propProviderId, rescheduleAppointmentId }) => {
  const toast = useToast();
  const { user } = useAuth();
  const formAssignmentService = FormAssignmentService.getInstance();
  const companyFormSettingsService = CompanyFormSettingsService.getInstance();
  const [resolvedProviderName, setResolvedProviderName] = useState<string>('');

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedProvider, setSelectedProvider] = useState<string>(propProviderId || '');
  const [providerAvailability, setProviderAvailability] = useState<ProviderAvailability[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string; endTime: string } | null>(null);
  const [selectedAppointmentType, setSelectedAppointmentType] = useState<'check-in' | 'therapy' | 'initial-consultation' | 'couples-initial' | 'couples'>('therapy');
  const [isEstablishedPatient, setIsEstablishedPatient] = useState(false);
  const [activeCouple, setActiveCouple] = useState<CoupleRecord | null>(null);
  const [canBookAppointment, setCanBookAppointment] = useState(true);
  const [pendingRequiredForms, setPendingRequiredForms] = useState<string[]>([]);
  const [blockingEnabled, setBlockingEnabled] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<{
    sessionType: 'initial-consultation' | 'follow-up' | 'therapy' | 'assessment' | 'couples' | 'other';
    modality: 'video' | 'in-person';
    reason: string;
  }>({
    sessionType: 'therapy',
    modality: 'video',
    reason: ''
  });

  // Sync provider ID from props
  useEffect(() => {
    if (propProviderId && propProviderId !== selectedProvider) {
      setSelectedProvider(propProviderId);
    }
  }, [propProviderId]);

  // Resolve provider name for confirmation emails
  useEffect(() => {
    if (!selectedProvider) return;
    const lookupName = async () => {
      try {
        const provDoc = await getDoc(doc(db, 'providers', selectedProvider));
        if (provDoc.exists()) {
          const d = provDoc.data();
          setResolvedProviderName(`${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Your Provider');
        }
      } catch { /* non-critical */ }
    };
    lookupName();
  }, [selectedProvider]);

  // Check if patient is in an active couple
  useEffect(() => {
    if (!patientId) return;
    const unsub = FirebaseCoupleService.subscribeToPatientCouples(patientId, (couples) => {
      const active = couples.find(c => c.status === 'active');
      setActiveCouple(active || null);
    });
    return () => unsub();
  }, [patientId]);

  useEffect(() => {
    if (selectedProvider) {
      loadProviderData();
      const init = async () => {
        const established = await checkPatientStatus();
        await checkFormCompletionStatus(established);
      };
      init();
    }
  }, [selectedProvider, selectedDate, patientId]);

  const loadProviderData = async () => {
    if (!selectedProvider) return;

    try {
      // Load availability from Firestore (client has read access)
      const availability = await firebaseAppointmentService.getProviderAvailability(selectedProvider);
      setProviderAvailability(availability.filter(a => a.isActive));

      // Load client's own appointments (query by patientId — client has permission)
      const myAppointments = await firebaseAppointmentService.getAppointments({ patientId });
      setAppointments(myAppointments);
    } catch (error) {
      console.error('Failed to load provider data from Firestore:', error);
      setProviderAvailability([]);
      setAppointments([]);
    }
  };

  const checkPatientStatus = async (): Promise<boolean> => {
    try {
      // Check if patient is marked as established (e.g., transferred from another provider)
      const patientDoc = await getDoc(doc(db, 'patients', patientId));
      const isMarkedEstablished = patientDoc.exists() && patientDoc.data()?.isEstablished === true;

      // Check if patient has any previous appointments (query by patientId only)
      const patientAppointments = await firebaseAppointmentService.getAppointments({ patientId });
      const hasPreviousAppointments = patientAppointments.some(apt =>
        apt.status !== 'cancelled' && apt.status !== 'no-show'
      );

      const established = hasPreviousAppointments || isMarkedEstablished;
      setIsEstablishedPatient(established);

      if (!established) {
        setSelectedAppointmentType('initial-consultation');
      } else {
        setSelectedAppointmentType('therapy');
      }
      return established;
    } catch (error) {
      console.error('Failed to check patient status:', error);
      setIsEstablishedPatient(false);
      setSelectedAppointmentType('initial-consultation');
      return false;
    }
  };

  const checkFormCompletionStatus = async (established?: boolean) => {
    const DEFAULT_COMPANY_ID = 'default-company';
    // Use passed value (fresh from checkPatientStatus) or fall back to state
    const isPatientEstablished = established !== undefined ? established : isEstablishedPatient;

    try {
      // Check if booking block is enabled for this company (still synchronous)
      const isBlockingEnabled = companyFormSettingsService.shouldBlockBooking(DEFAULT_COMPANY_ID);
      setBlockingEnabled(isBlockingEnabled);

      if (!isBlockingEnabled || isPatientEstablished) {
        setCanBookAppointment(true);
        setPendingRequiredForms([]);
        return;
      }

      // Get required form template IDs (still synchronous)
      const requiredFormIds = companyFormSettingsService.getAutoAssignForms(DEFAULT_COMPANY_ID);

      // Get all form assignments for this patient (now async)
      const assignments = await formAssignmentService.getAssignmentsByClientId(patientId);

      // Find which required forms are not yet completed
      const incompleteForms: string[] = [];

      requiredFormIds.forEach(formId => {
        const assignment = assignments.find(a => a.formTemplateId === formId);
        if (!assignment || !assignment.completed) {
          // Get form template name
          const FormTemplateService = require('../../../services/FormTemplateService').default;
          const templateService = FormTemplateService.getInstance();
          const template = templateService.getFormTemplate(formId);
          if (template) {
            incompleteForms.push(template.name);
          }
        }
      });

      setPendingRequiredForms(incompleteForms);
      setCanBookAppointment(incompleteForms.length === 0);
    } catch (error) {
      console.error('Failed to check form completion status:', error);
      // On error, allow booking to avoid blocking the user unnecessarily
      setCanBookAppointment(true);
      setPendingRequiredForms([]);
    }
  };

  const handleSlotClick = (startTime: string, endTime: string) => {
    // Check if user can book before allowing them to select a slot
    if (!canBookAppointment && blockingEnabled) {
      toast.warning('Forms Incomplete', 'Please complete all required forms before booking an appointment.');
      return;
    }

    setSelectedSlot({ startTime, endTime });
    setShowBookingModal(true);
  };

  const handleBookAppointment = async () => {
    if (!selectedSlot) return;

    // Map appointment type to duration and sessionType
    let duration = 60;
    let sessionType: 'initial-consultation' | 'follow-up' | 'therapy' | 'assessment' | 'couples' | 'other' = 'therapy';

    if (selectedAppointmentType === 'check-in') {
      duration = 30;
      sessionType = 'follow-up';
    } else if (selectedAppointmentType === 'therapy') {
      duration = 50;
      sessionType = 'therapy';
    } else if (selectedAppointmentType === 'initial-consultation') {
      duration = 90;
      sessionType = 'initial-consultation';
    } else if (selectedAppointmentType === 'couples-initial') {
      duration = 90;
      sessionType = 'initial-consultation';
    } else if (selectedAppointmentType === 'couples') {
      duration = 50;
      sessionType = 'couples';
    }

    const newAppointment: Record<string, any> = {
      patientId,
      patientName,
      providerId: selectedProvider,
      createdBy: patientId,
      appointmentDate: selectedDate,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      duration,
      modality: bookingDetails.modality,
      sessionType,
      status: 'scheduled' as const,
    };

    // Add couple data for couples appointments
    if ((selectedAppointmentType === 'couples-initial' || selectedAppointmentType === 'couples') && activeCouple) {
      newAppointment.coupleId = activeCouple.id;
      const isPatient1 = patientId === activeCouple.patient1Id;
      newAppointment.patient2Id = isPatient1 ? activeCouple.patient2Id : activeCouple.patient1Id;
      newAppointment.patient2Name = isPatient1 ? activeCouple.patient2Name : activeCouple.patient1Name;
    }

    // Only add optional fields if they have values — Firestore rejects undefined
    if (bookingDetails.reason) {
      newAppointment.appointmentReason = bookingDetails.reason;
    }
    if (bookingDetails.modality === 'video') {
      newAppointment.videoLink = `https://meet.clinic.com/${Date.now()}`;
    }
    if (bookingDetails.modality === 'in-person') {
      newAppointment.location = 'Main Office';
    }

    try {
      if (rescheduleAppointmentId) {
        // Move existing appointment to new date/time (no cancel + recreate)
        await firebaseAppointmentService.updateAppointment(rescheduleAppointmentId, {
          appointmentDate: newAppointment.appointmentDate,
          startTime: newAppointment.startTime,
          endTime: newAppointment.endTime,
          duration: newAppointment.duration,
          modality: newAppointment.modality,
          status: 'scheduled',
          ...(newAppointment.appointmentReason ? { appointmentReason: newAppointment.appointmentReason } : {}),
          ...(newAppointment.location ? { location: newAppointment.location } : {}),
        });
      } else {
        await firebaseAppointmentService.createAppointment(newAppointment as any);
      }

      // Log activity for provider's Practice Activity dashboard
      if (selectedProvider) {
        FirebaseActivityService.getInstance().logActivity({
          providerId: selectedProvider,
          clientId: patientId,
          clientName: patientName,
          activityType: 'appointment_scheduled',
          title: rescheduleAppointmentId ? 'Appointment rescheduled' : 'New appointment booked',
          description: `${patientName} ${rescheduleAppointmentId ? 'rescheduled' : 'booked'} a ${sessionType} session for ${selectedDate.toLocaleDateString()}.`,
          metadata: {
            appointmentDate: selectedDate.toISOString(),
            appointmentType: bookingDetails.modality as 'video' | 'in-person',
          }
        }).catch(err => console.warn('[Activity] Appointment log failed:', err));
      }

      setShowBookingModal(false);
      setSelectedSlot(null);
      await loadProviderData();

      // Send confirmation email + SMS (non-blocking)
      const clientEmail = user?.email;
      const formattedDate = selectedDate.toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
      const confirmParams = {
        clientName: patientName,
        providerName: resolvedProviderName || 'Your Provider',
        appointmentDate: formattedDate,
        startTime: formatTime12Hour(selectedSlot.startTime),
        modality: bookingDetails.modality as 'video' | 'in-person',
      };
      const emailService = EmailNotificationService.getInstance();

      if (clientEmail) {
        emailService.sendAppointmentConfirmation({
          ...confirmParams,
          clientEmail,
          sessionType,
        }).then(sent => {
          if (sent) console.log('[Email] Appointment confirmation sent');
        }).catch(err => console.warn('[Email] Confirmation failed:', err));
      }

      // SMS confirmation — look up patient phone from Firestore
      try {
        const patientDoc = await getDoc(doc(db, 'patients', patientId));
        const patientPhone = patientDoc.exists() ? patientDoc.data().phone : '';
        if (patientPhone) {
          emailService.sendAppointmentConfirmationSMS({
            ...confirmParams,
            clientPhone: patientPhone,
          }).then(sent => {
            if (sent) console.log('[SMS] Appointment confirmation sent');
          }).catch(err => console.warn('[SMS] Confirmation failed:', err));
        }
      } catch { /* non-critical */ }

      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to book appointment:', error);
      toast.error('Booking Failed', 'Could not book appointment. Please try again.');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ margin: '0 0 24px 0', color: '#f9fafb' }}>Book an Appointment</h2>

      {/* Required Forms Warning */}
      {blockingEnabled && !canBookAppointment && pendingRequiredForms.length > 0 && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '2px solid #dc2626',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ fontSize: '24px', color: '#dc2626' }}>⚠️</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#991b1b', fontSize: '18px', fontWeight: 700 }}>
                Required Forms Incomplete
              </h3>
              <p style={{ margin: '0 0 12px 0', color: '#7f1d1d', fontSize: '15px', lineHeight: '1.6' }}>
                You must complete the following required intake forms before booking an appointment:
              </p>
              <ul style={{ margin: '0 0 16px 0', paddingLeft: '24px', color: '#7f1d1d' }}>
                {pendingRequiredForms.map((formName, index) => (
                  <li key={index} style={{ marginBottom: '6px', fontSize: '15px' }}>
                    <strong>{formName}</strong>
                  </li>
                ))}
              </ul>
              <p style={{ margin: 0, color: '#7f1d1d', fontSize: '14px' }}>
                Please visit the <strong>"Forms to Complete"</strong> section on your dashboard to fill out these forms.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Message */}
      <div style={{
        backgroundColor: '#1f2937',
        border: '1px solid #6CA6C1',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px',
        color: '#d1d5db',
        fontSize: '14px'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#6CA6C1' }}>
          How to Book:
        </p>
        <p style={{ margin: 0 }}>
          1. Select an available date (green) from the calendar below<br />
          2. Choose a time slot<br />
          3. Confirm your appointment details
        </p>
      </div>

      {/* 3-Month Calendar */}
      <div style={{ marginBottom: '24px' }}>
        <MultiMonthCalendar
          providerId={selectedProvider}
          onDateSelect={(date) => setSelectedDate(date)}
          selectedDate={selectedDate}
          availabilityData={providerAvailability}
        />
      </div>

      {/* Appointment Type Selector */}
      {selectedDate && (
        <div style={{
          backgroundColor: '#1f2937',
          border: '2px solid #6CA6C1',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#6CA6C1', marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>
            What type of appointment do you need?
          </h3>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {isEstablishedPatient ? (
              <>
                {/* Check-in / Follow-up */}
                <div
                  onClick={() => {
                    console.log('Check-in clicked');
                    setSelectedAppointmentType('check-in');
                  }}
                  style={{
                    flex: '1 1 calc(50% - 6px)',
                    minWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 12px',
                    backgroundColor: selectedAppointmentType === 'check-in' ? '#28527A' : '#1f2937',
                    border: selectedAppointmentType === 'check-in' ? '2px solid #6CA6C1' : '2px solid #4b5563',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'center'
                  }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: '2px solid #6CA6C1',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selectedAppointmentType === 'check-in' ? '#6CA6C1' : 'transparent'
                  }}>
                    {selectedAppointmentType === 'check-in' && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#1e293b'
                      }} />
                    )}
                  </div>
                  <div style={{ color: '#f9fafb', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                    Check-in / Follow-up
                  </div>
                  <div style={{ color: '#d1d5db', fontSize: '13px' }}>30 minutes</div>
                </div>

                {/* Regular Therapy Session */}
                <div
                  onClick={() => {
                    console.log('Therapy clicked');
                    setSelectedAppointmentType('therapy');
                  }}
                  style={{
                    flex: '1 1 calc(50% - 6px)',
                    minWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 12px',
                    backgroundColor: selectedAppointmentType === 'therapy' ? '#28527A' : '#1f2937',
                    border: selectedAppointmentType === 'therapy' ? '2px solid #6CA6C1' : '2px solid #4b5563',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'center'
                  }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: '2px solid #6CA6C1',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selectedAppointmentType === 'therapy' ? '#6CA6C1' : 'transparent'
                  }}>
                    {selectedAppointmentType === 'therapy' && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#1e293b'
                      }} />
                    )}
                  </div>
                  <div style={{ color: '#f9fafb', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                    Regular Therapy Session
                  </div>
                  <div style={{ color: '#d1d5db', fontSize: '13px' }}>50 minutes</div>
                </div>

                {/* Couples Session — only shown if patient is in an active couple */}
                {activeCouple && (
                  <div
                    onClick={() => setSelectedAppointmentType('couples')}
                    style={{
                      flex: '1 1 100%',
                      minWidth: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 12px',
                      backgroundColor: selectedAppointmentType === 'couples' ? '#28527A' : '#1f2937',
                      border: selectedAppointmentType === 'couples' ? '2px solid #7C3AED' : '2px solid #4b5563',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: '2px solid #7C3AED',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: selectedAppointmentType === 'couples' ? '#7C3AED' : 'transparent'
                    }}>
                      {selectedAppointmentType === 'couples' && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1e293b' }} />
                      )}
                    </div>
                    <div style={{ color: '#f9fafb', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                      Couples Session
                    </div>
                    <div style={{ color: '#d1d5db', fontSize: '13px' }}>
                      50 minutes &middot; with {FirebaseCoupleService.getPartnerName(activeCouple, patientId)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Initial Consultation options for new patients */
              <>
                {/* Individual Initial Consultation */}
                <div
                  onClick={() => setSelectedAppointmentType('initial-consultation')}
                  style={{
                    flex: activeCouple ? '1 1 calc(50% - 6px)' : '1',
                    minWidth: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 12px',
                    backgroundColor: selectedAppointmentType === 'initial-consultation' ? '#28527A' : '#1f2937',
                    border: selectedAppointmentType === 'initial-consultation' ? '2px solid #6CA6C1' : '2px solid #4b5563',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'center'
                  }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: '2px solid #6CA6C1',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: selectedAppointmentType === 'initial-consultation' ? '#6CA6C1' : 'transparent'
                  }}>
                    {selectedAppointmentType === 'initial-consultation' && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1e293b' }} />
                    )}
                  </div>
                  <div style={{ color: '#f9fafb', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                    Individual Initial Consultation
                  </div>
                  <div style={{ color: '#d1d5db', fontSize: '13px' }}>90 minutes</div>
                </div>

                {/* Couples Initial Consultation — only shown if patient is in an active couple */}
                {activeCouple && (
                  <div
                    onClick={() => setSelectedAppointmentType('couples-initial')}
                    style={{
                      flex: '1 1 calc(50% - 6px)',
                      minWidth: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px 12px',
                      backgroundColor: selectedAppointmentType === 'couples-initial' ? '#28527A' : '#1f2937',
                      border: selectedAppointmentType === 'couples-initial' ? '2px solid #7C3AED' : '2px solid #4b5563',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'center'
                    }}>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: '2px solid #7C3AED',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: selectedAppointmentType === 'couples-initial' ? '#7C3AED' : 'transparent'
                    }}>
                      {selectedAppointmentType === 'couples-initial' && (
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1e293b' }} />
                      )}
                    </div>
                    <div style={{ color: '#f9fafb', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                      Couples Initial Consultation
                    </div>
                    <div style={{ color: '#d1d5db', fontSize: '13px' }}>
                      90 minutes &middot; with {FirebaseCoupleService.getPartnerName(activeCouple, patientId)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Selected Date Time Slots */}
      {selectedDate && (
        <div>
          <h3 style={{ color: '#f9fafb', marginBottom: '16px' }}>
            Available Times for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>
          <TimeBlockView
            selectedDate={selectedDate}
            availability={providerAvailability}
            appointments={appointments}
            onSlotClick={handleSlotClick}
            showPatientNames={false}
            readOnly={false}
            hideOverrideButton={true}
            providerId={selectedProvider}
            useFirestore={true}
            appointmentDuration={
              selectedAppointmentType === 'initial-consultation' || selectedAppointmentType === 'couples-initial' ? 90 :
              selectedAppointmentType === 'check-in' ? 30 : 50
            }
            filterByAppointmentType={selectedAppointmentType}
          />
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedSlot && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            border: '2px solid #6CA6C1',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              color: '#f9fafb',
              fontSize: '20px'
            }}>
              Confirm Appointment
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Date & Time</div>
              <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 'bold' }}>
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
              <div style={{ color: '#6CA6C1', fontSize: '16px', marginTop: '4px' }}>
                {formatTime12Hour(selectedSlot.startTime)} - {formatTime12Hour(selectedSlot.endTime)}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '4px' }}>Appointment Type</div>
              <div style={{ color: '#f9fafb', fontSize: '16px', fontWeight: 'bold' }}>
                {selectedAppointmentType === 'check-in' && 'Check-in / Follow-up (30 min)'}
                {selectedAppointmentType === 'therapy' && 'Regular Therapy Session (50 min)'}
                {selectedAppointmentType === 'initial-consultation' && 'Initial Consultation (90 min)'}
                {selectedAppointmentType === 'couples-initial' && 'Couples Initial Consultation (90 min)'}
                {selectedAppointmentType === 'couples' && 'Couples Session (50 min)'}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                How would you like to meet? *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setBookingDetails({ ...bookingDetails, modality: 'video' })}
                  style={{
                    padding: '16px',
                    backgroundColor: bookingDetails.modality === 'video' ? '#3b82f6' : '#374151',
                    color: '#f9fafb',
                    border: bookingDetails.modality === 'video' ? '2px solid #60a5fa' : '2px solid #4b5563',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    fontSize: '14px',
                    fontWeight: bookingDetails.modality === 'video' ? 'bold' : 'normal'
                  }}
                >
                  <div style={{ fontSize: '24px', marginBottom: '6px' }}>📹</div>
                  <div>VIDEO CALL</div>
                </button>

                <button
                  type="button"
                  onClick={() => setBookingDetails({ ...bookingDetails, modality: 'in-person' })}
                  style={{
                    padding: '16px',
                    backgroundColor: bookingDetails.modality === 'in-person' ? '#10b981' : '#374151',
                    color: '#f9fafb',
                    border: bookingDetails.modality === 'in-person' ? '2px solid #34d399' : '2px solid #4b5563',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    fontSize: '14px',
                    fontWeight: bookingDetails.modality === 'in-person' ? 'bold' : 'normal'
                  }}
                >
                  <div style={{ fontSize: '24px', marginBottom: '6px' }}>🏥</div>
                  <div>IN-PERSON</div>
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#e5e7eb' }}>
                Reason for Visit (Optional)
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <textarea
                  value={bookingDetails.reason}
                  onChange={(e) => setBookingDetails({ ...bookingDetails, reason: e.target.value })}
                  placeholder="Brief description..."
                  rows={3}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#374151',
                    color: '#f9fafb',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    resize: 'vertical'
                  }}
                />
                <VoiceInputButton onResult={(text) => setBookingDetails(prev => ({ ...prev, reason: prev.reason ? prev.reason + ' ' + text : text }))} size={18} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleBookAppointment}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Confirm Booking
              </button>
              <button
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedSlot(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#374151',
                  color: '#f9fafb',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && selectedSlot && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#1f2937',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            border: '2px solid #10b981',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              ✓
            </div>
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#10b981',
              fontSize: '20px',
              fontWeight: 'bold'
            }}>
              {rescheduleAppointmentId ? 'Appointment rescheduled successfully!' : 'Appointment booked successfully!'}
            </h3>

            <div style={{
              backgroundColor: '#374151',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px' }}>Date</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                  {selectedDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ color: '#d1d5db', fontSize: '12px' }}>Time</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>{selectedSlot.startTime}</div>
              </div>
              <div>
                <div style={{ color: '#d1d5db', fontSize: '12px' }}>Type</div>
                <div style={{ color: '#f9fafb', fontSize: '14px' }}>
                  {bookingDetails.modality === 'video' ? 'Video Session' : 'In-Person Visit'}
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowSuccessModal(false);
                setSelectedSlot(null);
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#6CA6C1',
                color: '#1e293b',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointmentTab;
